import React, { useEffect, useState } from "react";
import getEmployeesForService from "@/dataLayer/getEmployeesForService";
import { ServiceModalEmployeeSelection } from "./serviceModalEmployeeSelection";
import { ServiceModalModifierSelection } from "./serviceModalModifierSelection";
import { v4 as uuidv4 } from "uuid";
import { addToServiceCart } from "./serviceSummary";
import { ClearCartModal } from "@/components/clearCartModal/clearCartModal";
import { getFromLocalStorage } from "@/utils/localStorageUtils";
import { RetailCartItem } from "@/dto/retailCartItem";
import { GiftCard } from "@/dto/giftCard";
import { Modifier } from "@/dto/modifer";
import { removeFromSessionStorage } from "@/utils/sessionStorageUtils";
import { LocalStorageKey, SessionStorageKey } from "@/utils/storageKeys";
import { RegularModal } from "@/components/modals/modals";

export const ServiceModal = ({
  merchantName,
  isCheckoutEnabled,
  isOpen,
  onRequestClose,
  service,
  modifiers,
  merchantId,
  menu,
  isAcceptingAppointments,
  merchantPreferences,
  merchantColor,
}: any) => {
  const [showEmployeeSelection, setShowEmployeeSelection] =
    useState<boolean>(false);
  const [employeeList, setEmployeeList] = useState<any[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<string>("");
  const [selectedModifiers, setSelectedModifiers] = useState<Modifier[]>([]);
  const [quantity, setQuantity] = useState<number | undefined>(1);
  const [finalPrice, setFinalPrice] = useState(service.price);
  const [isClearCartOpen, setIsClearCartOpen] = useState<boolean>(false);

  useEffect(() => {
    if (selectedEmployee === "default" && employeeList.length === 1) {
      handleAddToServiceCart();
    }
  }, [selectedEmployee, employeeList]);

  const openClearCart = () => {
    setIsClearCartOpen(true);
  };

  const handleAddToServiceCart = () => {
    const selectedEmployeeDetails = employeeList.find(
      (employee: any) => employee.employeeUuid === selectedEmployee
    );

    const retailCart = getFromLocalStorage<RetailCartItem[]>(
      LocalStorageKey.CART,
      []
    );
    const giftCard = getFromLocalStorage<GiftCard | null>(
      LocalStorageKey.GIFT_CARD,
      null
    );

    const newService = {
      id: service.id,
      name: service.name,
      price: service.price,
      quantity: quantity,
      modifiers: Object.values(selectedModifiers).flat(),
      specialInstructions: "",
      subtotal: finalPrice,
      total: finalPrice,
      cartId: uuidv4(),
      employee: selectedEmployeeDetails,
      serviceDetails: service,
    };

    if (retailCart.length >= 1 || giftCard) {
      openClearCart();
    } else {
      addToServiceCart(newService);
      window.dispatchEvent(
        new StorageEvent("storage", { key: "service-cart" })
      );
      onRequestClose();
    }
  };

  const handleClearCartClose = () => {
    setIsClearCartOpen(false);
  };

  async function onShowEmployeeSelection() {
    removeFromSessionStorage(SessionStorageKey.PICKUP_TIME);
    const serviceEmployees = await getEmployeesForService(
      merchantId,
      service.id
    );
    const defaultEmployee = {
      name: "Any available staff",
      employeeUuid: "default",
      itemUuid: service.id,
    };
    if (
      serviceEmployees?.length != 0 &&
      !merchantPreferences.removeStaffFromSite
    ) {
      if (
        serviceEmployees?.length == 1 ||
        !merchantPreferences.anyAvailableStaff
      ) {
        setEmployeeList(serviceEmployees);
        setSelectedEmployee(serviceEmployees[0].employeeUuid);
        setShowEmployeeSelection(true);
      } else {
        setEmployeeList([defaultEmployee, ...serviceEmployees]);
        setSelectedEmployee(defaultEmployee.employeeUuid);
        setShowEmployeeSelection(true);
      }
    } else {
      setEmployeeList([defaultEmployee]);
      setSelectedEmployee(defaultEmployee.employeeUuid);
    }
  }

  const handleEmployeeSelect = (employeeUuid: string) => {
    setSelectedEmployee(employeeUuid);
  };

  const productImage =
    service.images && service.images.length > 0
      ? service.images.reduce((prev: any, current: any) => {
          if (
            current.name.includes("xxxhdpi") ||
            (current.name.includes("xxhdpi") &&
              !prev.name.includes("xxxhdpi")) ||
            (current.name.includes("xhdpi") &&
              !prev.name.includes("xxhdpi") &&
              !prev.name.includes("xxxhdpi"))
          ) {
            return current;
          }
          return prev;
        }, service.images[0])
      : null;

  return (
    <>
      {isClearCartOpen && (
        <ClearCartModal
          isOpen={isClearCartOpen}
          onRequestClose={handleClearCartClose}
          merchantColor={merchantColor}
          itemType={service?.itemType}
          addToCart={handleAddToServiceCart}
        />
      )}
      {!isClearCartOpen && (
        <RegularModal
          isOpen={isOpen}
          onRequestClose={onRequestClose}
          overlayClassName="bg-opacity-0"
        >
          {!showEmployeeSelection && (
            <ServiceModalModifierSelection
              isCheckoutEnabled={isCheckoutEnabled}
              merchantName={merchantName}
              onRequestClose={onRequestClose}
              productImage={productImage}
              service={service}
              formatDuration={formatDuration}
              onShowEmployeeSelection={onShowEmployeeSelection}
              menu={menu}
              modifiers={modifiers}
              selectedModifiers={selectedModifiers}
              setSelectedModifiers={setSelectedModifiers}
              setFinalPrice={setFinalPrice}
              isAcceptingAppointments={isAcceptingAppointments}
              merchantColor={merchantColor}
              quantity={quantity}
              setQuantity={setQuantity}
            />
          )}
          {showEmployeeSelection && (
            <ServiceModalEmployeeSelection
              onRequestClose={onRequestClose}
              productImage={productImage}
              service={service}
              formatDuration={formatDuration}
              employeeList={employeeList}
              handleEmployeeSelect={handleEmployeeSelect}
              selectedEmployee={selectedEmployee}
              setShowEmployeeSelection={setShowEmployeeSelection}
              handleAddToServiceCart={handleAddToServiceCart}
              finalPrice={finalPrice}
              merchantPreferences={merchantPreferences}
              merchantColor={merchantColor}
            />
          )}
        </RegularModal>
      )}
    </>
  );
};

export const formatDuration = (duration: string) => {
  if (!duration) return "";
  const regex = /PT(?:(\d+)H)?(?:(\d+)M)?/;
  const matches = duration.match(regex);
  const hours =
    matches?.[1] && parseInt(matches[1]) > 0 ? `${matches[1]} hr` : "";
  const minutes =
    matches?.[2] && parseInt(matches[2]) > 0 ? `${matches[2]} min` : "";
  return `${hours}${hours && minutes ? " " : ""}${minutes}`.trim();
};
